import {API_URL} from '@services/api.config';
import {appendUploadAccessToken} from './uploadAuthUrl';

const IMAGE_EXT = /\.(jpe?g|png|webp|gif)$/i;

export const pickProfilePhotoRaw = source =>
    source?.foto_perfil_url ??
    source?.fotoPerfilUrl ??
    source?.foto_perfil ??
    source?.profile_photo_url ??
    source?.avatar_url ??
    null;

const getFrontOrigin = () => {
    if (typeof window === 'undefined') {
        return '';
    }

    return window.location.origin;
};

const getApiHostOrigin = () => {
    const apiBase = String(API_URL || '').trim();

    if (!apiBase) {
        return getFrontOrigin();
    }

    if (apiBase.startsWith('/')) {
        const backendOrigin = process.env.REACT_APP_BACKEND_ORIGIN;

        if (backendOrigin) {
            try {
                return new URL(backendOrigin).origin;
            } catch (error) {
                // seguir
            }
        }

        return getFrontOrigin();
    }

    try {
        const apiUrl = new URL(apiBase, getFrontOrigin());
        return `${apiUrl.protocol}//${apiUrl.host}`;
    } catch (error) {
        return getFrontOrigin();
    }
};

/** Rutas relativas bajo /uploads/profile_photos/ */
export const normalizeUploadsPath = rawUrl => {
    if (rawUrl == null || rawUrl === '') {
        return null;
    }

    let normalized = String(rawUrl).trim().replace(/\\/g, '/');

    if (!normalized || normalized === 'null' || normalized === 'undefined') {
        return null;
    }

    try {
        if (/^https?:\/\//i.test(normalized) || normalized.startsWith('//')) {
            const parsed = new URL(
                normalized.startsWith('//') ? `${window.location.protocol}${normalized}` : normalized
            );
            normalized = parsed.pathname;
        }
    } catch (error) {
        // seguir
    }

    if (!normalized.startsWith('/')) {
        normalized = `/${normalized}`;
    }

    normalized = normalized.replace(/^\/api(?=\/uploads\/)/, '');

    if (normalized.startsWith('/uploads/')) {
        return normalized.split('?')[0];
    }

    if (normalized.startsWith('/profile_photos/')) {
        return `/uploads${normalized}`.split('?')[0];
    }

    const profileIdx = normalized.indexOf('/profile_photos/');

    if (profileIdx >= 0) {
        const tail = normalized.slice(profileIdx);

        return tail.startsWith('/uploads')
            ? tail.split('?')[0]
            : `/uploads${tail}`.split('?')[0];
    }

    if (normalized.startsWith('profile_photos/')) {
        return `/uploads/${normalized}`.split('?')[0];
    }

    const fileName = normalized.replace(/^\//, '').split('/').pop();

    if (fileName && IMAGE_EXT.test(fileName)) {
        return `/uploads/profile_photos/${fileName}`;
    }

    return null;
};

export const getProfilePhotoOrigins = () => [...new Set([getFrontOrigin(), getApiHostOrigin()].filter(Boolean))];

/** Rutas a probar (incluye variante /api/uploads para proxy en dev). */
export const getProfilePhotoPathVariants = rawUrl => {
    const canonical = normalizeUploadsPath(rawUrl);

    if (!canonical) {
        return [];
    }

    const variants = new Set([canonical]);

    if (canonical.startsWith('/uploads/')) {
        variants.add(canonical.replace(/^\/uploads/, '/api/uploads'));
    }

    return [...variants];
};

export const buildProfilePhotoCandidates = (rawUrl, {bust = false} = {}) => {
    if (!rawUrl) {
        return [];
    }

    const trimmed = String(rawUrl).trim();

    if (/^blob:/i.test(trimmed) || /^data:image\//i.test(trimmed)) {
        return [trimmed];
    }

    const pathVariants = getProfilePhotoPathVariants(trimmed);

    if (!pathVariants.length) {
        return [];
    }

    const bustParam = bust ? `v=${Date.now()}` : '';
    const urls = [];

    getProfilePhotoOrigins().forEach(origin => {
        pathVariants.forEach(path => {
            let url = `${origin}${path}`;
            if (bustParam) {
                url += (url.includes('?') ? '&' : '?') + bustParam;
            }
            urls.push(appendUploadAccessToken(url));
        });
    });

    return [...new Set(urls)];
};

export const resolveProfilePhotoUrl = (rawUrl, options = {}) => {
    const candidates = buildProfilePhotoCandidates(rawUrl, options);

    return candidates[0] || null;
};

export const profilePhotoSrc = (rawUrl, options = {}) =>
    resolveProfilePhotoUrl(rawUrl, options);

/** Comprueba si alguna URL de la foto responde en el navegador. */
export const canLoadProfilePhoto = rawUrl =>
    new Promise(resolve => {
        const candidates = buildProfilePhotoCandidates(rawUrl, {bust: true});

        if (!candidates.length) {
            resolve(false);
            return;
        }

        let pending = candidates.length;
        let settled = false;

        const finish = ok => {
            if (settled) {
                return;
            }

            if (ok) {
                settled = true;
                resolve(true);
                return;
            }

            pending -= 1;
            if (pending <= 0) {
                settled = true;
                resolve(false);
            }
        };

        candidates.forEach(url => {
            const img = new Image();
            img.onload = () => finish(true);
            img.onerror = () => finish(false);
            img.src = url;
        });
    });
